import { useState, useCallback, useEffect, useRef } from 'react';
import { ResolvedMatch, Employee } from '../interfaces/interface';
import { logger } from '../../../../logger';
import { EmployeeService } from '../services/EmployeeService';
import { getEmployeeByLogin } from 'src/api/papi-service';
import { PapiEmployeeMapper } from '../utils/papiEmployeeMapper';
import {
  sortAttendees,
  isDuplicateEmployee,
  findDuplicateEmails as findDuplicates,
  getExistingEmails as getEmails,
  hasUnresolvedNames as hasUnresolved
} from '../utils/attendeeUtils';

export const useAttendeeManagement = (userAlias?: string) => {
  const [resolvedAttendees, setResolvedAttendees] = useState<ResolvedMatch[]>([]);
  const [hasAddedCurrentUser, setHasAddedCurrentUser] = useState<boolean>(false);
  const addingUserRef = useRef<boolean>(false);

  /**
   * Fetches complete employee data from PAPI when the assistant ID is missing.
   * This ensures we have all employee fields including the EA reference.
   */
  const fetchCompleteEmployeeData = useCallback(async (employee: Employee): Promise<Employee> => {
    if (!employee.employee_assistant_employee_id && employee.login_name) {
      try {
        logger.info('Fetching complete employee data to get EA ID');

        const employeeResponse = await getEmployeeByLogin(employee.login_name);

        if (!employeeResponse.employee) {
          logger.warn('Employee data not found in PAPI');
          return employee;
        }

        return PapiEmployeeMapper.mapToEmployee(employeeResponse.employee);
      } catch (error) {
        logger.error('Failed to fetch complete employee data', { error });
      }
    }
    return employee;
  }, []);

  // Auto-add current user as optional attendee on mount
  useEffect(() => {
    const addCurrentUser = async () => {
      if (!userAlias || hasAddedCurrentUser || addingUserRef.current) {
        return;
      }

      addingUserRef.current = true;

      try {
        const employeeData = await getEmployeeByLogin(userAlias);

        if (employeeData.employee) {
          const currentUser = PapiEmployeeMapper.mapToEmployee(employeeData.employee);

          if (currentUser.employee_internal_email_address) {
            logger.info('Auto-adding current user as optional attendee');

            const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(currentUser);

            const currentUserAttendee: ResolvedMatch = {
              inputName: currentUser.employee_name,
              matches: [currentUser],
              selectedEmployee: currentUser,
              selectedExecutiveAssistant,
              status: 'exact',
              priority: 'optional',
              id: `attendee-current-user-${Date.now()}`,
              resolvedEmail: currentUser.employee_internal_email_address
            };

            setResolvedAttendees((prevAttendees) => {
              if (isDuplicateEmployee(currentUser, prevAttendees)) {
                logger.info('Current user already exists in attendees list, skipping auto-add');
                setHasAddedCurrentUser(true);
                return prevAttendees;
              }

              setHasAddedCurrentUser(true);
              return sortAttendees([...prevAttendees, currentUserAttendee]);
            });
          } else {
            logger.warn('Current user has no valid email address');
            setHasAddedCurrentUser(true);
          }
        } else {
          logger.warn('Current user data not found, will retry on next effect run');
        }
      } catch (error) {
        logger.error('Failed to auto-add current user, will retry on next effect run', { error });
      } finally {
        addingUserRef.current = false;
      }
    };

    const timer = setTimeout(addCurrentUser, 500);
    return () => clearTimeout(timer);
  }, [userAlias, hasAddedCurrentUser]);

  const handleAddAttendee = useCallback(
    async (employee: Employee, priority: 'required' | 'optional') => {
      logger.info('handleAddAttendee called', {
        hasEaId: !!employee.employee_assistant_employee_id,
        priority
      });

      if (isDuplicateEmployee(employee, resolvedAttendees)) {
        logger.warn('Attempt to add duplicate attendee');
        return;
      }

      const enrichedEmployee = await fetchCompleteEmployeeData(employee);

      if (!enrichedEmployee) {
        logger.error('Failed to enrich employee data');
        return;
      }

      const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(enrichedEmployee);

      const newId = `attendee-${Date.now()}-${Math.random()}`;
      const newAttendee: ResolvedMatch = {
        inputName: enrichedEmployee.employee_name,
        matches: [enrichedEmployee],
        selectedEmployee: enrichedEmployee,
        selectedExecutiveAssistant,
        status: 'exact',
        priority,
        id: newId,
        resolvedEmail: enrichedEmployee.employee_internal_email_address
      };

      setResolvedAttendees((prev) => sortAttendees([...prev, newAttendee]));
    },
    [resolvedAttendees, fetchCompleteEmployeeData]
  );

  const handleAttendeeUpdate = useCallback(
    async (id: string, updates: Partial<ResolvedMatch>) => {
      if (updates.selectedEmployee) {
        logger.info('handleAttendeeUpdate called with selectedEmployee', {
          hasEaId: !!updates.selectedEmployee.employee_assistant_employee_id
        });

        const enrichedEmployee = await fetchCompleteEmployeeData(updates.selectedEmployee);

        if (!enrichedEmployee) {
          logger.error('Failed to enrich employee data during update');
          return;
        }

        try {
          const selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(enrichedEmployee);

          updates = {
            ...updates,
            selectedEmployee: enrichedEmployee,
            selectedExecutiveAssistant
          };

          logger.info('Executive assistant lookup completed for manual employee selection', {
            hasExecutiveAssistant: !!selectedExecutiveAssistant
          });
        } catch (error) {
          logger.warn('Failed to lookup executive assistant for manual employee selection', { error });
        }
      }

      setResolvedAttendees((prev) => {
        return prev.map((attendee) => (attendee.id === id ? { ...attendee, ...updates } : attendee));
      });
    },
    [fetchCompleteEmployeeData]
  );

  const handleAttendeeRemove = useCallback((id: string) => {
    setResolvedAttendees((prev) => prev.filter((attendee) => attendee.id !== id));
  }, []);

  const replaceDocumentAttendees = useCallback((newDocumentAttendees: ResolvedMatch[]) => {
    setResolvedAttendees((prevAttendees) => {
      const manualAttendees = prevAttendees.filter((attendee) => attendee.id.startsWith('attendee-'));

      const filteredDocumentAttendees = newDocumentAttendees.filter((docAttendee) => {
        return !isDuplicateEmployee(docAttendee.selectedEmployee || ({} as Employee), manualAttendees);
      });

      const combinedAttendees = [...manualAttendees, ...filteredDocumentAttendees];
      return sortAttendees(combinedAttendees);
    });
  }, []);

  const getExistingEmails = useCallback(() => {
    return getEmails(resolvedAttendees);
  }, [resolvedAttendees]);

  const getRequiredAttendees = useCallback(() => {
    return resolvedAttendees.filter((a) => a.priority === 'required');
  }, [resolvedAttendees]);

  const getOptionalAttendees = useCallback(() => {
    return resolvedAttendees.filter((a) => a.priority === 'optional');
  }, [resolvedAttendees]);

  const findDuplicateEmails = useCallback(() => {
    return findDuplicates(resolvedAttendees);
  }, [resolvedAttendees]);

  const hasUnresolvedNames = useCallback(() => {
    return hasUnresolved(resolvedAttendees);
  }, [resolvedAttendees]);

  return {
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeUpdate,
    handleAttendeeRemove,
    replaceDocumentAttendees,
    getExistingEmails,
    getRequiredAttendees,
    getOptionalAttendees,
    findDuplicateEmails,
    hasUnresolvedNames,
    setResolvedAttendees
  };
};
